/**
 * @module useResultProcessing
 * @description Handles grade calculation and result table processing for student results.
 * Transforms raw student result data from the GraphQL API into a format suitable for display
 * in report cards and result views.
 *
 * @example
 * ```typescript
 * const studentResult = ref(graphqlResult?.studentResult);
 * const { grades, calculateGrade, tableHeaders, processedSubjects } = useResultProcessing(studentResult);
 *
 * // Display grades in template
 * grades.value.forEach(g => console.log(g.letter, g.range));
 *
 * // Calculate grade for a score
 * const grade = calculateGrade(85); // Returns 'B' or grade letter
 * const remark = calculateGrade(85, 'remark'); // Returns 'Very Good'
 * ```
 *
 * @param studentResult - Ref containing the raw student result from GraphQL `studentResult` query
 *
 * @returns
 * - `grades`: Computed array of grade bands from the result template's grade group
 * - `calculateGrade(score, outputType)`: Function to calculate grade letter or remark from a score
 * - `tableHeaders`: Computed array of visible column headers for the result table
 * - `processedSubjects`: Computed array of processed subject rows with calculated values
 *
 * @used_by
 * - ViewResults.vue (via useViewResults composable)
 * - Any component displaying student result data
 */
import type { Grade } from '@/types/composition-functions';

interface ResultHeader {
    key: string;
    label: string;
    type?: string;
    is_visible: boolean;
}

interface ResultTemplateColumn {
    key: string;
    label: string;
    type?: string;
    calculation_arguments?: string | Record<string, unknown>;
}

interface StudentResultData {
    headers?: ResultHeader[];
    data?: Record<string, unknown>[];
    template?: {
        grade_group?: {
            gradeBands?: Grade[];
        };
        columns?: ResultTemplateColumn[];
        properties?: Record<string, unknown>;
    };
}

export function useResultProcessing(studentResult: Ref<StudentResultData | null | undefined>, chartColumnLabel: Ref<string> = ref('')) {

  const grades = computed(() => {
    const bands = studentResult.value?.template?.grade_group?.gradeBands || [];
    if (bands.length === 0) {
      // Fallback if no grading system found (or keep empty)
      return [
        { letter: 'A', range: '90 - 100', minimum: 90, maximum: 100, name: 'Excellent' },
        { letter: 'B', range: '80 - 89', minimum: 80, maximum: 89, name: 'Very Good' },
        { letter: 'C', range: '70 - 79', minimum: 70, maximum: 79, name: 'Good' },
        { letter: 'D', range: '60 - 69', minimum: 60, maximum: 69, name: 'Fair' },
        { letter: 'E', range: '50 - 59', minimum: 50, maximum: 59, name: 'Pass' },
        { letter: 'F', range: 'Below 50', minimum: 0, maximum: 49, name: 'Fail' },
      ];
    }

    // Sort by minimum score descending
    return [...bands].sort((a: Grade, b: Grade) => (b.minimum ?? 0) - (a.minimum ?? 0)).map((g: Grade) => ({
      letter: g.grade,
      range: `${g.minimum} - ${g.maximum}`,
      minimum: g.minimum,
      maximum: g.maximum,
      name: g.name,
    }));
  });

  function calculateGrade(score: number, outputType: 'grade' | 'remark' = 'grade') {
    const numericScore = Math.round(Number(score));
    if (isNaN(numericScore)) return '-';

    const gradeBand = grades.value.find((g) => numericScore >= g.minimum && numericScore <= g.maximum);

    if (!gradeBand) return '-';

    return outputType === 'remark' ? (gradeBand.name || '-') : (gradeBand.letter || '-');
  }

  const tableHeaders = computed(() => {
    if (!studentResult.value?.headers) return [];
    const dynamicHeaders = studentResult.value.headers
      .filter((h: ResultHeader) => h.is_visible)
      .map((h: ResultHeader) => h.label);
    if (studentResult.value?.data?.some(row => row.module_name) && !dynamicHeaders.includes('Module')) dynamicHeaders.push('Module');
    const courseKeys = ['course_code', 'course_title', 'subject_code', 'subject_name'];
    const hasCourseColumn = studentResult.value.headers.some(
      (h: ResultHeader) => h.is_visible && courseKeys.includes(h.key),
    );
    return hasCourseColumn ? dynamicHeaders : ['Subject', ...dynamicHeaders];
  });

  const processedSubjects = computed(() => {
    if (!studentResult.value?.data || !studentResult.value?.headers) return [];

    // Create a map of column configurations for easy lookup
    const columnConfigMap = new Map();
    if (studentResult.value.template?.columns) {
      studentResult.value.template.columns.forEach((col: ResultTemplateColumn) => {
        columnConfigMap.set(col.key, col);
      });
    }

    const courseKeys = ['course_code', 'course_title', 'subject_code', 'subject_name'];
    const hasCourseColumn = studentResult.value.headers!.some(
      (h: ResultHeader) => h.is_visible && courseKeys.includes(h.key),
    );

    return studentResult.value.data.map((row: Record<string, unknown>) => {
      const orderedRow: Record<string, unknown> = {};

      if (!hasCourseColumn) {
        const subject = row.subject as { name?: string } | undefined;
        orderedRow['Subject'] = row.subject_name || subject?.name || row.name || 'Unknown Subject';
      }

            // 2. Dynamic Columns
            studentResult.value!.headers!.forEach((h: ResultHeader) => {
              if (!h.is_visible) return;

              let cellValue = '-';

              // If it's a Grade column, calculate it
              // We check if the header has type 'GRADE' OR if the match template column is 'GRADE'
              const templateCol = columnConfigMap.get(h.key);
              const isGradeCol = h.type === 'GRADE' || templateCol?.type === 'GRADE';

              // Priority: 1. Existing Value (from Backend) 2. Calculation (fallback)
              const existingValue = row[h.key];
              if (existingValue !== undefined && existingValue !== null && existingValue !== '') {
                cellValue = existingValue;
              } else if (isGradeCol && templateCol?.calculation_arguments) {
                let args = templateCol.calculation_arguments;

                // Handle case where args might be a string (JSON) or an object
                if (typeof args === 'string') {
                  try {
                    args = JSON.parse(args);
                  } catch {
                    // Malformed JSON — leave args as the raw string; grade calc is skipped below.
                  }
                }

                // Ensure args is an object before accessing its properties
                if (typeof args === 'object' && args !== null && 'source' in args) {
                  const sourceKey = args.source;
                  const sourceValue = row[sourceKey];

                  if (sourceValue !== undefined && sourceValue !== null) {
                    cellValue = calculateGrade(sourceValue, args.output || 'grade');
                  }
                }
              } else {
                // Standard column
                cellValue = existingValue !== undefined && existingValue !== null ? existingValue : '-';
              }

              orderedRow[h.label] = cellValue;
            });

            if (tableHeaders.value.includes('Module')) orderedRow.Module = row.module_name ?? '—';
            return orderedRow;
    });
  });

  const chartData = computed(() => {
    if (!processedSubjects.value.length) return null;

    // chartColumnLabel now receives the column 'key' directly, not 'label'
    let targetLabel: string | null = null;
    if (studentResult.value?.headers && chartColumnLabel.value) {
      const header = studentResult.value.headers.find((h: ResultHeader) => h.key === chartColumnLabel.value);
      if (header) {
        targetLabel = header.label;
      }
    }

    const totalHeader = tableHeaders.value.find(h => targetLabel && h === targetLabel);

    if (!totalHeader) return null;

    const labels = processedSubjects.value.map((s: Record<string, unknown>) => s['Subject']);
    const data = processedSubjects.value.map((s: Record<string, unknown>) => {
      const val = s[totalHeader];
      return typeof val === 'number' ? val : parseFloat(val) || 0;
    });
    return {
      labels,
      datasets: [
        {
          label: totalHeader,
          data,
          backgroundColor: 'rgba(16, 185, 129, 0.2)', // emerald-500 with opacity
          borderColor: '#10b981', // emerald-500
          borderWidth: 2,
          pointBackgroundColor: '#10b981',
        },
      ],
    };
  });

  function moduleHeadingAt(index: number): string {
    const rows = studentResult.value?.data ?? [];
    const row = rows[index];
    if (row?.module_result_mode !== 'separate') return '';
    return index === 0 || row.programme_module_id !== rows[index - 1]?.programme_module_id
      ? String(row.module_name ?? 'Unassigned / historical courses') : '';
  }

  return {
    moduleHeadingAt,
    grades,
    calculateGrade,
    tableHeaders,
    processedSubjects,
    chartData,
  };
}
